<template>
  <div class="name-step">
    <h2 class="step-title">Your Name</h2>
    <p class="step-sub">A name is the first thing seekers will feel. Choose the one that resonates.</p>

    <div class="name-cards">
      <button
        v-for="item in normalizedNames"
        :key="item.name"
        class="name-card"
        :class="{ selected: chosenName === item.name }"
        @click="selectName(item.name)"
      >
        <span class="name-text">{{ item.name }}</span>
        <span v-if="item.rationale" class="name-rationale">{{ item.rationale }}</span>
      </button>
    </div>

    <div class="custom-name-section">
      <input
        v-model="customName"
        placeholder="Or enter your own..."
        class="custom-name-input"
        @input="handleCustomInput"
        @focus="emit('context-signal', { panel: 'studio', view: 'name_step', interaction: 'custom_name_focused', data: {} })"
        @blur="checkAvailability"
      />
      <span v-if="nameAvailable === true" class="avail-ok">Available</span>
      <span v-if="nameAvailable === false" class="avail-no">{{ nameReason }}</span>
    </div>

    <div v-if="activeName" class="subdomain-preview">
      <span class="subdomain-label">Your mystic's home:</span>
      <span class="subdomain-url">{{ slugify(activeName) }}.mysticaida.ai</span>
    </div>

    <div class="step-actions">
      <button class="primary-btn" :disabled="!activeName" @click="proceed">
        Continue &rarr;
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import axios from 'axios'
import { static_store } from '@/store'

const props = defineProps({
  provisioningId: Number,
  nameSuggestions: Array,
  chatAction: { type: Object, default: null },
})
const emit = defineEmits(['name-chosen', 'next', 'context-signal'])

// Local copy of names that can be updated via chat action
const localNames = ref([...(props.nameSuggestions || [])])

watch(() => props.nameSuggestions, (newVal) => {
  localNames.value = [...(newVal || [])]
})

// Normalize: support both plain strings and {name, rationale} dicts
const normalizedNames = computed(() => {
  if (!localNames.value) return []
  return localNames.value.map(item => {
    if (typeof item === 'string') return { name: item, rationale: '' }
    return { name: item.name || '', rationale: item.rationale || '' }
  })
})

// Watch for chat-triggered name attempts and lens-based updates
watch(() => props.chatAction, (action) => {
  if (action?.type === 'try_name' && action.name) {
    customName.value = action.name
    chosenName.value = ''
    checkAvailability()
  }
  if (action?.type === 'update_names' && action.names) {
    localNames.value = action.names
    chosenName.value = ''
    customName.value = ''
  }
})

const chosenName = ref('')
const customName = ref('')
const nameAvailable = ref(null)
const nameReason = ref('')

const activeName = computed(() => customName.value.trim() || chosenName.value)

function slugify(str) {
  return str.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
}

function authHeaders() {
  const token = localStorage.getItem('authToken')
  return token ? { Authorization: `Token ${token}` } : {}
}

function selectName(name) {
  chosenName.value = name
  customName.value = ''
  nameAvailable.value = null
  emit('name-chosen', name)
  emit('context-signal', {
    panel: 'studio', view: 'name_step', interaction: 'name_selected',
    data: { name, subdomain: slugify(name) },
  })
  saveNameToServer(name)
}

function handleCustomInput() {
  chosenName.value = ''
  nameAvailable.value = null
}

async function saveNameToServer(name) {
  try {
    await axios.post(
      `${static_store.apiUrl}/reader/become-mystic/update/${props.provisioningId}/`,
      { chosen_name: name },
      { headers: authHeaders() }
    )
  } catch { /* silent */ }
}

async function checkAvailability() {
  const name = customName.value.trim()
  if (!name) { nameAvailable.value = null; return }
  try {
    await axios.post(
      `${static_store.apiUrl}/reader/become-mystic/update/${props.provisioningId}/`,
      { chosen_name: name },
      { headers: authHeaders() }
    )
    nameAvailable.value = true
    emit('name-chosen', name)
    emit('context-signal', {
      panel: 'studio', view: 'name_step', interaction: 'custom_name_available',
      data: { name, subdomain: slugify(name) },
    })
  } catch (e) {
    nameAvailable.value = false
    nameReason.value = e.response?.data?.error || 'Not available'
    emit('context-signal', {
      panel: 'studio', view: 'name_step', interaction: 'custom_name_taken',
      data: { name },
    })
  }
}

async function proceed() {
  const name = activeName.value
  if (name) {
    await saveNameToServer(name)
    emit('name-chosen', name)
    emit('next')
  }
}
</script>

<style scoped>
.name-step { color: #E8D8FF; }
.step-title {
  font-family: 'Cinzel', serif; font-size: 24px; font-weight: 600; color: #FFDFA3;
  margin: 0 0 8px; padding: 0; border: none; border-radius: 0;
  background: none; box-shadow: none; text-align: left;
}
.step-sub { font-size: 13px; color: rgba(255,255,255,0.45); line-height: 1.65; margin-bottom: 28px; }
.name-cards { display: flex; flex-direction: column; gap: 10px; margin-bottom: 16px; }
.name-card {
  display: flex; flex-direction: column; gap: 4px;
  background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.1);
  border-radius: 10px; padding: 14px 20px;
  cursor: pointer; font-family: inherit; transition: all 0.2s; text-align: left;
}
.name-card:hover { border-color: rgba(201,169,78,0.4); }
.name-card.selected { background: rgba(201,169,78,0.1); border-color: rgba(201,169,78,0.5); }
.name-text { font-size: 16px; color: rgba(232,216,255,0.8); font-family: 'Cinzel', serif; }
.name-card.selected .name-text { color: #FFDFA3; }
.name-rationale { font-size: 11px; color: rgba(255,255,255,0.35); font-style: italic; }
.custom-name-section { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; }
.custom-name-input {
  flex: 1; background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.12); border-radius: 8px;
  padding: 12px 14px; color: #E8D8FF; font-size: 14px; font-family: inherit;
}
.custom-name-input:focus { outline: none; border-color: rgba(201,169,78,0.4); }
.avail-ok { font-size: 12px; color: #7DE8CC; white-space: nowrap; }
.avail-no { font-size: 12px; color: rgba(255,120,120,0.8); white-space: nowrap; }
.subdomain-preview {
  margin-bottom: 24px; padding: 12px 16px;
  background: rgba(201,169,78,0.06); border: 1px solid rgba(201,169,78,0.2); border-radius: 8px;
}
.subdomain-label { font-size: 11px; color: rgba(195,169,255,0.5); display: block; margin-bottom: 4px; }
.subdomain-url { font-family: 'Cinzel', serif; font-size: 14px; color: #C9A94E; letter-spacing: 0.02em; }
.step-actions { margin-top: 8px; }
.primary-btn {
  width: 100%; padding: 14px;
  background: rgba(201,169,78,0.12); border: 1px solid rgba(201,169,78,0.35);
  border-radius: 10px; color: #C9A94E; font-size: 15px; font-weight: 500;
  cursor: pointer; font-family: 'Cinzel', serif; transition: all 0.2s;
}
.primary-btn:hover:not(:disabled) { background: rgba(201,169,78,0.22); }
.primary-btn:disabled { opacity: 0.4; cursor: not-allowed; }
</style>
